import { useFlags } from 'launchdarkly-react-client-sdk';
import { FC, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useQuery } from 'react-query';
import {
  createSearchParams,
  useLocation,
  useNavigate,
  useParams,
  useSearchParams,
} from 'react-router-dom';

import {
  QBadge,
  QBodyLayout,
  QBox,
  QButtonGroup,
  QHeader,
  QHorizontalMetadata,
  QInheritedFromParentIcon,
  QLastModifiedMetadata,
  QOpenPropertiesPanelButton,
  QOwnerMetadata,
  QPageLoader,
  QResourceRouteTabs,
  QRouteTabs,
  QStack,
  QTag,
  QText,
  QTitle,
  QVersionMetadata,
  navigateAcrossMFEs,
  useCurrentUser,
  useFeatureFlag,
  useToastProvider,
} from '@qualio/ui-components';

import { documentApi } from '../../../api/document';
import { GroupApi } from '../../../api/group';
import { DocumentFormat, DocumentStatus } from '../../../api/model/document';
import { TagApi } from '../../../api/tag';
import { tokenApi } from '../../../api/token';
import { userApi } from '../../../api/user';

import {
  currentUserHasAccessToDocument,
  getUserCommentsPermission,
  isCrossCompanyDocument,
} from '../../../util/CurrentUser';
import {
  convertApiChangeControlToViewableInstance,
  convertApiDocumentToViewableInstance,
} from '../../../util/DocumentApiUtils';
import {
  EditorMode,
  mapEditorModeToIntentString,
  mapIntentStringToEditorMode,
} from '../../../util/EditModeUtils';
import { DocumentStatusIdToStatusConfigMap } from '../../../util/WorkspaceUtils';

import { DocumentStatusBanner } from '../DocumentStatusBanner/index';
import {
  ApprovalControls,
  AutoSaveNotification,
  ButtonsContextWrapper,
  CreateNewDraftButton,
  DisplayVersionSelect,
  EditButton,
  EditSuggestToggle,
  ExportDocumentButton,
  MakeEffectiveButton,
  MarkAsEditedButton,
  MarkAsReviewedButton,
  MoreActionsButton,
  PeriodicReviewButton,
  RevertToDraftButton,
  ReviewButton,
  SaveAndExitButton,
  SendForApprovalButton,
  SendForReviewButton,
  SuggestButton,
  SummarizeButton,
  UndoDeleteButton,
} from './Buttons';
import { DocumentProperties } from './DocumentProperties';

import {
  DocumentOverviewContext,
  DocumentOverviewContextType,
  EditorStatus,
  EditorStatusContext,
  EditorStatusContextType,
} from './Context';
import {
  canUserEditDocument,
  canUserSuggestOnDocument,
  shouldRenderCCTab,
  shouldRenderChangeOrientationButton,
  shouldRenderCompareAgainst,
  shouldRenderTrainingTab,
  shouldRenderVersionSelector,
  isVersionRestrictedForUser,
  sortVersions,
} from './RenderLogic';

import { VersionSelect } from '../DocumentCompareVersions';

import { getDocumentQris } from '@qualio/search-qri';
import { changeControlApi } from '../../../api/changeControl';
import { useBodyBackground } from '../../../hooks/BodyBackground';
import { useChangeControlWithTemplate } from '../../../hooks/useChangeControlWithTemplate';
import { useDocumentTitle } from '../../../hooks/useDocumentTitle';
import { initialiseGlobalEditedSections } from '../../../util/ActivityLogUtils';
import { delayedRedirectToDashboard } from '../../../util/PageUtils';
import { toAbsoluteUrl } from '../../../util/UrlUtils';
import { getOwnerText } from '../../../util/UserUtils';
import { BannerPlaceholder } from '../BannerPlaceholder';
import { NotFound } from '../NotFound';
import { ChangeOrientationButton } from './Buttons/ChangeOrientationButton';
import { ZoomContext } from './Context';
import styles from './DocumentOverview.module.css';
import { useIsNarrowEditorViewport, useNextVersionDetails } from './hooks';
import { QMultiRootEditorContextProvider } from './QMultiRootEditor/Context';
import { OverviewRoutes, OverviewTabs } from './routes';
import { ChangeControlTab, DocumentTab, TrainingAssessmentTab } from './Tabs';
import { ZoomDropdown } from './ZoomDropdown';
import { useQriServiceUrl } from '../../../hooks/useQriServiceUrl';

export { OverviewRoutes, OverviewTabs };

interface VersionSelectorStackProps {
  editorMode: EditorMode;
  currentUser: ReturnType<typeof useCurrentUser>;
  qualioDocument: ReturnType<typeof convertApiDocumentToViewableInstance>;
  latestVersion: ReturnType<typeof sortVersions>[number] | null;
  displayVersion: string;
  versions: ReturnType<typeof sortVersions>;
  handleDisplayVersionOptionChange: (
    isLatest: boolean,
    docId: number,
    version: string,
  ) => void;
  handleCompareVersionsOptionChange: (version: string) => void;
}

const VersionSelectorStack: FC<VersionSelectorStackProps> = ({
  editorMode,
  currentUser,
  qualioDocument,
  latestVersion,
  displayVersion,
  versions,
  handleDisplayVersionOptionChange,
  handleCompareVersionsOptionChange,
}) => {
  const { showDocumentVersions } = useFlags();
  if (editorMode !== EditorMode.VIEW) {
    return null;
  }
  return (
    <QStack direction="row" ml="auto" spacing={2}>
      {shouldRenderVersionSelector(currentUser, showDocumentVersions) && (
        <DisplayVersionSelect
          latestVersion={latestVersion ? latestVersion.version : latestVersion}
          handleOptionChange={handleDisplayVersionOptionChange}
          version={displayVersion}
          versions={versions}
          isVersionDisabled={(version) =>
            isVersionRestrictedForUser(currentUser, version)
          }
        />
      )}
      {shouldRenderCompareAgainst(
        qualioDocument,
        currentUser,
        latestVersion,
      ) && (
        <VersionSelect
          LHSVersion={displayVersion}
          latestVersion={latestVersion!.version}
          variant="RHS"
          handleOptionChange={handleCompareVersionsOptionChange}
          version=""
          allVersions={versions}
        />
      )}
    </QStack>
  );
};

interface DocumentTabsSectionProps {
  qualioDocument: ReturnType<typeof convertApiDocumentToViewableInstance>;
  currentUser: ReturnType<typeof useCurrentUser>;
  editorMode: EditorMode;
  updateEditorMode: (mode: EditorMode) => void;
  isMultiRootEditorEnabled: boolean | undefined;
  latestVersion: ReturnType<typeof sortVersions>[number] | null;
  displayVersion: string;
  versions: ReturnType<typeof sortVersions>;
  handleDisplayVersionOptionChange: VersionSelectorStackProps['handleDisplayVersionOptionChange'];
  handleCompareVersionsOptionChange: VersionSelectorStackProps['handleCompareVersionsOptionChange'];
}

const DocumentFormatZoomDropdown: FC<{
  qualioDocument: DocumentTabsSectionProps['qualioDocument'];
}> = ({ qualioDocument }) =>
  qualioDocument.document_format === DocumentFormat.QualioNativeDocument ? (
    <ZoomDropdown />
  ) : null;

const ChangeOrientationSlot: FC<
  Pick<
    DocumentTabsSectionProps,
    'qualioDocument' | 'currentUser' | 'editorMode' | 'isMultiRootEditorEnabled'
  >
> = ({ qualioDocument, currentUser, editorMode, isMultiRootEditorEnabled }) => {
  if (
    !shouldRenderChangeOrientationButton(
      qualioDocument,
      currentUser,
      editorMode,
      isMultiRootEditorEnabled,
    )
  ) {
    return null;
  }
  return <ChangeOrientationButton />;
};

const presenceListMarginLeft = (
  editorMode: EditorMode,
  qualioDocument: DocumentTabsSectionProps['qualioDocument'],
  isMultiRootEditorEnabled: boolean | undefined,
): string => {
  const isDraftMultiRoot =
    qualioDocument.status_id === DocumentStatus.Draft &&
    !isMultiRootEditorEnabled;
  return editorMode === EditorMode.VIEW || isDraftMultiRoot ? '2px' : 'auto';
};

interface ResourceRouteTabsSectionProps extends DocumentTabsSectionProps {
  documentsTaskDrawer: boolean;
  location: ReturnType<typeof useLocation>;
}

const ResourceRouteTabsSection: FC<ResourceRouteTabsSectionProps> = ({
  qualioDocument,
  currentUser,
  editorMode,
  updateEditorMode,
  isMultiRootEditorEnabled,
  latestVersion,
  displayVersion,
  versions,
  handleDisplayVersionOptionChange,
  handleCompareVersionsOptionChange,
  documentsTaskDrawer,
  location,
}) => {
  const { t } = useTranslation();
  const qriServiceUrl = useQriServiceUrl();

  return (
    <>
      <QResourceRouteTabs
        data-cy="tab-and-version-select"
        qri={getDocumentQris(
          {
            companyId: qualioDocument.company_id,
            documentMatrixId: qualioDocument.document_matrix_id,
            id: qualioDocument.id,
          },
          qriServiceUrl,
        )}
        showTasks={documentsTaskDrawer}
        sticky={false}
      >
        <QBox className="cc-presence-list"></QBox>
        <QRouteTabs.Item
          route={OverviewRoutes.CONTENT}
          data-cy={'document-tab'}
        >
          <QText>{t('labels.document')}</QText>
        </QRouteTabs.Item>
        {shouldRenderCCTab(qualioDocument, currentUser) && (
          <QRouteTabs.Item
            route={OverviewRoutes.CHANGE_CONTROL}
            data-cy={'change-control-tab'}
          >
            <QText>{t('activityHistory.changeControl')}</QText>
          </QRouteTabs.Item>
        )}
        {shouldRenderTrainingTab(qualioDocument, currentUser) && (
          <QRouteTabs.Item
            route={OverviewRoutes.TRAINING_ASSESSMENT}
            data-cy={'training-tab'}
          >
            <QText>{t('labels.trainingAssessment')}</QText>
          </QRouteTabs.Item>
        )}
        <EditSuggestToggle setEditorMode={updateEditorMode} />
        <VersionSelectorStack
          editorMode={editorMode}
          currentUser={currentUser}
          qualioDocument={qualioDocument}
          latestVersion={latestVersion}
          displayVersion={displayVersion}
          versions={versions}
          handleDisplayVersionOptionChange={handleDisplayVersionOptionChange}
          handleCompareVersionsOptionChange={handleCompareVersionsOptionChange}
        />
        <ChangeOrientationSlot
          qualioDocument={qualioDocument}
          currentUser={currentUser}
          editorMode={editorMode}
          isMultiRootEditorEnabled={isMultiRootEditorEnabled}
        />
        <DocumentFormatZoomDropdown qualioDocument={qualioDocument} />
        <QBox
          ml={presenceListMarginLeft(
            editorMode,
            qualioDocument,
            isMultiRootEditorEnabled,
          )}
          className="doc-overview-presence-list"
        ></QBox>
      </QResourceRouteTabs>
      <QBox
        id="document-overview-tab-panels"
        className={`${styles['document-overview-tabs']}`}
      >
        <QBox
          hidden={!location.pathname.endsWith(`/${OverviewRoutes.CONTENT}`)}
        >
          <DocumentTab isMultiRootEditorEnabled={isMultiRootEditorEnabled} />
        </QBox>
        {shouldRenderCCTab(qualioDocument, currentUser) && (
          <QBox
            hidden={
              !location.pathname.endsWith(`/${OverviewRoutes.CHANGE_CONTROL}`)
            }
          >
            <ChangeControlTab />
          </QBox>
        )}
        {shouldRenderTrainingTab(qualioDocument, currentUser) && (
          <QBox
            hidden={
              !location.pathname.endsWith(
                `/${OverviewRoutes.TRAINING_ASSESSMENT}`,
              )
            }
          >
            <TrainingAssessmentTab />
          </QBox>
        )}
      </QBox>
    </>
  );
};

export const DocumentOverview = () => {
  const { t } = useTranslation();
  useBodyBackground('document-editor-overview-refresh', true);
  const { id } = useParams();
  const [searchParams, setSearchParams] = useSearchParams();
  const intent = searchParams.get('intent');
  const requestedDocVersion = searchParams.get('version');
  const requestedTab = searchParams.get('tab');
  const currentUser = useCurrentUser();
  const { showToast } = useToastProvider();
  const [commentsPermission, setCommentsPermission] = useState<number>();
  const [requestedDocumentVersion, setRequestedDocumentVersion] = useState<
    string | null
  >(requestedDocVersion);
  const location = useLocation();
  const navigate = useNavigate();
  const [docEditorStatus, setDocEditorStatus] = useState<EditorStatus>(
    EditorStatus.STABLE,
  );
  const [ccEditorStatus, setCCEditorStatus] = useState<EditorStatus>(
    EditorStatus.STABLE,
  );
  const [trainingAssessmentStatus, setTrainingAssessmentStatus] =
    useState<EditorStatus>(EditorStatus.STABLE);

  const { documentsTaskDrawer, emailInstanceSwitch } = useFlags();
  const featureFlags = useFeatureFlag();
  const enterpriseAccounts = Boolean(featureFlags['enterprise-accounts']);

  const setCkeditorTranslations = useCallback(() => {
    window.CKEDITOR_TRANSLATIONS = window.CKEDITOR_TRANSLATIONS || {};
    window.CKEDITOR_TRANSLATIONS['en'] =
      window.CKEDITOR_TRANSLATIONS['en'] || {};
    window.CKEDITOR_TRANSLATIONS['en'].dictionary =
      window.CKEDITOR_TRANSLATIONS['en'].dictionary || {};

    Object.assign(window.CKEDITOR_TRANSLATIONS['en'].dictionary, {
      'Reply...': t('comments.replyOrMentionOthersUsingAt'),
      'Write a comment...': t('comments.mentionOrComment'),
      PENDING_ACTION_SENDING_DATA: t('comments.savingPendingChanges'),
    });
  }, [t]);

  setCkeditorTranslations();

  useEffect(() => {
    setRequestedDocumentVersion(requestedDocVersion);
  }, [requestedDocVersion]);

  const loadDocument = useCallback(async () => {
    if (!id) {
      return;
    }
    try {
      const fetchedDocument = await documentApi.fetchByIdOrVersion(
        id,
        requestedDocVersion ?? '',
        1,
        1,
      );
      return convertApiDocumentToViewableInstance(fetchedDocument);
    } catch (e: any) {
      if (
        e.response?.data?.slug === 'document-access-restricted-or-not-found'
      ) {
        void navigate('/missing', { replace: true });
      } else if (
        emailInstanceSwitch &&
        e.response?.data?.slug === 'raise-access-in-wrong-instance'
      ) {
        const searchParams = new URLSearchParams({ qri: e.response.data.qri });
        navigateAcrossMFEs(`/switch-instance?${searchParams}`);
      } else {
        showToast({
          'data-cy': 'document-overview-fetch-document-failure-error-toast',
          id: 'fetch-document-failure',
          status: 'error',
          title: t('notifications.failedToFetchDocument'),
          description: t('errors.errorOccurredWithDocument'),
          replace: true,
        });
      }
    }
  }, [id, requestedDocVersion, showToast, navigate, t, emailInstanceSwitch]);

  const {
    isLoading: isLoadingDocument,
    isFetching: isFetchingDocument,
    data: qualioDocument,
    refetch: refetchDocument,
  } = useQuery({
    queryKey: ['newDocument', id, requestedDocumentVersion],
    queryFn: loadDocument,
    refetchOnWindowFocus: false,
  });

  const [scale, setScale] = useState(1);
  const widthRef = useRef<HTMLDivElement>(null);

  useDocumentTitle(
    qualioDocument ? `${qualioDocument.code} ${qualioDocument.title}` : '',
  );

  const isCrossCompanyDoc =
    !!qualioDocument && isCrossCompanyDocument(currentUser, qualioDocument);

  const { data: nextVersion } = useNextVersionDetails(
    qualioDocument,
    currentUser.companyId,
    !isCrossCompanyDoc,
  );

  const [editorMode, setEditorMode] = useState<EditorMode>(EditorMode.VIEW);
  const isNarrowViewport = useIsNarrowEditorViewport();
  const aiButtonsInKebab = isNarrowViewport && editorMode === EditorMode.VIEW;

  useEffect(() => {
    initialiseGlobalEditedSections();
  }, []);

  const isMultiRootEditorEnabled =
    qualioDocument?.editor_version?.startsWith('multiroot');

  const updateEditorMode = useCallback(
    (mode: EditorMode) => {
      setSearchParams((prevParams) => {
        prevParams.set('intent', mapEditorModeToIntentString(mode));
        return prevParams;
      });
      setEditorMode(mode);
    },
    [setEditorMode, setSearchParams],
  );

  useEffect(() => {
    if (isMultiRootEditorEnabled) {
      return;
    }
    if (qualioDocument?.status_id && intent) {
      const canEdit = canUserEditDocument(qualioDocument, currentUser);
      const canSuggest = canUserSuggestOnDocument(qualioDocument, currentUser);
      const mode = mapIntentStringToEditorMode(intent);

      switch (true) {
        case intent === 'edit' && canEdit:
        case intent === 'suggest' && canSuggest:
          updateEditorMode(mode);
          break;
        default:
          updateEditorMode(EditorMode.VIEW);
      }
    }
  }, [
    currentUser,
    intent,
    qualioDocument,
    updateEditorMode,
    isMultiRootEditorEnabled,
  ]);

  const { data: periodicReview, refetch: refetchPeriodicReview } = useQuery({
    queryKey: ['periodicReview', id],
    queryFn: () =>
      documentApi.getPeriodicReviewByDocumentId(
        currentUser,
        qualioDocument!.id,
      ),
    refetchOnWindowFocus: false,
    enabled:
      !isCrossCompanyDoc &&
      qualioDocument?.status_id === DocumentStatus.Effective,
  });

  const {
    data: versions = [],
    isLoading: isLoadingVersions,
    isFetching: isFetchingVersions,
    refetch: refetchVersions,
  } = useQuery({
    queryKey: ['versions', id],
    queryFn: () => documentApi.getDocumentVersionsByDocumentId(+id!),
    refetchOnWindowFocus: false,
    enabled: !!qualioDocument,
  });

  const { data: users = [], isLoading: isLoadingUsers } = useQuery({
    queryKey: ['users'],
    queryFn: () => userApi.fetchUsers(currentUser.companyId),
    refetchOnWindowFocus: false,
  });

  const { data: tags = [], isLoading: isLoadingTags } = useQuery({
    queryKey: ['tags', currentUser.userId],
    queryFn: () => TagApi.fetchTags(currentUser),
    refetchOnWindowFocus: false,
    enabled: !!users.length,
  });

  const { data: groups = [] } = useQuery({
    queryKey: ['groups', currentUser.userId],
    queryFn: () => GroupApi.fetchGroups(),
    refetchOnWindowFocus: false,
  });

  const loadCollaborationToken = async () => {
    const currentCommentPermissions = getUserCommentsPermission(
      currentUser,
      qualioDocument as any,
    );
    setCommentsPermission(currentCommentPermissions);
    return tokenApi.fetch({ comments: currentCommentPermissions });
  };
  const {
    data: collaborationToken,
    isFetching: isFetchingToken,
    isLoading: isLoadingToken,
  } = useQuery({
    queryKey: [
      'collabToken',
      id,
      qualioDocument?.status_id,
      qualioDocument?.owner,
    ],
    queryFn: loadCollaborationToken,
    refetchOnWindowFocus: false,
    retry: 2,
    enabled:
      qualioDocument !== undefined && !isLoadingDocument && !isFetchingDocument,
    onError: () => {
      showToast({
        'data-cy': 'document-overview-fetch-collab-token-failures-error-toast',
        id: 'fetch-collab-token-failures',
        status: 'error',
        title: t('notifications.authenticationFailed'),
        description: t('errors.authenticationRedirectingToLogin'),
        replace: true,
      });
      delayedRedirectToDashboard();
    },
  });

  const fetchChangeControl = async () => {
    if (!id) {
      return null;
    }
    const changeControlResponse = await changeControlApi.fetchChangeControl(
      Number(id),
      currentUser.companyId,
    );
    if (changeControlResponse.length > 0) {
      const changeControl = changeControlResponse[0];
      return convertApiChangeControlToViewableInstance(changeControl);
    } else {
      return null;
    }
  };

  const {
    data: changeControl,
    refetch: refetchChangeControl,
    isLoading: isLoadingChangeControl,
    isFetching: isFetchingChangeControl,
  } = useQuery({
    queryFn: fetchChangeControl,
    queryKey: ['documentChangeControl', [id]],
    refetchOnWindowFocus: false,
    retry: false,
  });

  const refetchDocumentOverviewData = useCallback(
    (fetchLatestVersion = true) => {
      setEditorMode(EditorMode.VIEW);

      if (fetchLatestVersion) {
        setRequestedDocumentVersion(null);
      }
      void refetchDocument();
      void refetchVersions();
      void refetchChangeControl();
    },
    [refetchDocument, refetchVersions, refetchChangeControl],
  );

  const { changeControlWithTemplate } = useChangeControlWithTemplate(
    currentUser.companyId,
    changeControl?.id,
  );

  const displayVersion = useMemo(() => {
    return `${qualioDocument?.major_version}.${qualioDocument?.minor_version}`;
  }, [qualioDocument]);

  const documentOverviewContext: DocumentOverviewContextType = useMemo(
    () => ({
      qualioDocument: qualioDocument!,
      changeControl: changeControl!,
      changeControlWithTemplate,
      currentEditorMode: editorMode,
      commentsPermission: commentsPermission!,
      collaborationToken: collaborationToken!,
      refetchDocumentOverviewData,
      refetchChangeControl,
      updateEditorMode,
      users,
      tags,
      widthRef,
    }),
    [
      editorMode,
      qualioDocument,
      changeControl,
      changeControlWithTemplate,
      refetchDocumentOverviewData,
      refetchChangeControl,
      commentsPermission,
      collaborationToken,
      updateEditorMode,
      users,
      tags,
      widthRef,
    ],
  );

  const editorStatusContext: EditorStatusContextType = useMemo(
    () => ({
      docEditorStatus,
      ccEditorStatus,
      trainingAssessmentStatus,
      setCCEditorStatus,
      setDocEditorStatus,
      setTrainingAssessmentStatus,
    }),
    [
      docEditorStatus,
      ccEditorStatus,
      trainingAssessmentStatus,
      setCCEditorStatus,
      setDocEditorStatus,
      setTrainingAssessmentStatus,
    ],
  );

  const statusProps = useMemo(() => {
    if (qualioDocument?.status_id) {
      const config =
        DocumentStatusIdToStatusConfigMap[
          qualioDocument.status_id as DocumentStatus
        ];

      // Map status IDs to translation keys
      const statusKeyMap: { [key in DocumentStatus]: string } = {
        [DocumentStatus.Approved]: 'statuses.approved',
        [DocumentStatus.Approval_Declined]: 'statuses.approvalDeclined',
        [DocumentStatus.Deleted]: 'statuses.deleted',
        [DocumentStatus.Draft]: 'statuses.draft',
        [DocumentStatus.Effective]: 'statuses.effective',
        [DocumentStatus.For_Approval]: 'statuses.forApproval',
        [DocumentStatus.For_Periodic_Review]: 'statuses.forPeriodicReview',
        [DocumentStatus.For_Retirement]: 'statuses.forRetirement',
        [DocumentStatus.For_Review]: 'statuses.forReview',
        [DocumentStatus.Retired]: 'statuses.retired',
        [DocumentStatus.Superseded]: 'statuses.superseded',
      };

      return {
        ...config,
        label: t(statusKeyMap[qualioDocument.status_id as DocumentStatus]),
      };
    }
  }, [qualioDocument?.status_id, t]);

  const sortedVersions = useMemo(
    () => (versions ? sortVersions(versions) : []),
    [versions],
  );

  const latestVersion = useMemo(() => {
    return sortedVersions.length
      ? sortedVersions[sortedVersions.length - 1]
      : null;
  }, [sortedVersions]);

  const handleDisplayVersionOptionChange = useCallback(
    (isLatest: boolean, docId: number, version: string) => {
      const isMajorVersionSelected =
        versions.find(
          ({ version: selectedVersionStr }) => selectedVersionStr === version,
        )?.minor === '0';

      const isLatestOrMajorVersion = isLatest || isMajorVersionSelected;

      if (isLatestOrMajorVersion) {
        searchParams.delete('version');
        setSearchParams(searchParams);
        setRequestedDocumentVersion(null);
      } else {
        searchParams.set('version', version);
        setRequestedDocumentVersion(version);
        setSearchParams(searchParams);
      }

      if (docId.toString() !== id) {
        const newPath = location.pathname.replace(
          id as string,
          docId.toString(),
        );
        const params = new URLSearchParams();
        params.set('tab', requestedTab ?? OverviewTabs.DOCUMENT);
        if (!isLatestOrMajorVersion) {
          params.set('version', version);
        }
        void navigate({
          pathname: newPath,
          search: createSearchParams(params).toString(),
        });
      }
    },
    [
      id,
      location.pathname,
      navigate,
      requestedTab,
      searchParams,
      setSearchParams,
      versions,
    ],
  );

  const handleCompareVersionsOptionChange = useCallback(
    (version: string) => {
      if (version !== '') {
        const basePath = location.pathname.split(`/${id}`)[0];
        void navigate({
          pathname: `${basePath}/${id}/compare/${version}`,
          search: createSearchParams({
            version: `${qualioDocument?.major_version}.${qualioDocument?.minor_version}`,
          }).toString(),
        });
      }
    },
    [navigate, location.pathname, id, qualioDocument],
  );

  if (
    !qualioDocument ||
    isLoadingDocument ||
    isFetchingDocument ||
    isLoadingToken ||
    isFetchingToken ||
    isLoadingVersions ||
    isFetchingVersions ||
    isFetchingChangeControl ||
    isLoadingChangeControl ||
    isLoadingUsers ||
    isLoadingTags
  ) {
    return (
      <>
        <BannerPlaceholder />
        <QPageLoader />
      </>
    );
  }

  if (!currentUserHasAccessToDocument(currentUser, qualioDocument)) {
    return <NotFound />;
  }

  return (
    <DocumentOverviewContext.Provider value={documentOverviewContext}>
      <EditorStatusContext.Provider value={editorStatusContext}>
        <QMultiRootEditorContextProvider>
          <ZoomContext.Provider value={{ scale, setScale }}>
            <DocumentStatusBanner />
            <QBodyLayout.Default minWidth="unset">
              <QHeader showDivider={false}>
                <QTitle>
                  {qualioDocument.title}
                  {enterpriseAccounts && qualioDocument.parent_company && (
                    <QBox as="span" display="inline-flex" ml={2}>
                      <QInheritedFromParentIcon
                        parentName={qualioDocument.parent_company.name}
                        data-cy={`inherited-document-icon-${qualioDocument.id}`}
                      />
                    </QBox>
                  )}
                  <QBadge
                    ml={2}
                    data-cy="document-code"
                    copyValue={toAbsoluteUrl(
                      `/library/documents/${qualioDocument.id}`,
                    )}
                  >
                    {qualioDocument.code}
                  </QBadge>
                </QTitle>
                <QHorizontalMetadata>
                  <QTag
                    variantColor={statusProps?.color}
                    data-cy="document-status-label"
                  >
                    {statusProps?.label}
                  </QTag>
                  <QVersionMetadata
                    data-cy="document-version"
                    value={
                      qualioDocument.major_version +
                      '.' +
                      qualioDocument.minor_version
                    }
                  />
                  <QOwnerMetadata
                    data-cy="document-owner"
                    value={getOwnerText(qualioDocument)}
                  />
                  <QLastModifiedMetadata
                    data-cy="document-last-modified"
                    value={new Date(qualioDocument.modified_time * 1000)}
                  />
                </QHorizontalMetadata>
                <QButtonGroup>
                  <ButtonsContextWrapper>
                    <AutoSaveNotification />
                    {!aiButtonsInKebab && <ReviewButton />}
                    {!aiButtonsInKebab && (
                      <SummarizeButton documentCode={qualioDocument.code} />
                    )}
                    <ExportDocumentButton />
                    {!isMultiRootEditorEnabled && (
                      <>
                        <SuggestButton setEditorMode={updateEditorMode} />
                        <EditButton setEditorMode={updateEditorMode} />
                        <SaveAndExitButton setEditorMode={updateEditorMode} />
                      </>
                    )}
                    <UndoDeleteButton versions={versions} />
                    <CreateNewDraftButton periodicReview={periodicReview} />
                    <MakeEffectiveButton />
                    <SendForReviewButton />
                    <PeriodicReviewButton
                      periodicReview={periodicReview}
                      users={users}
                      refetchPeriodicReview={refetchPeriodicReview}
                      nextVersion={nextVersion}
                    />
                    <RevertToDraftButton />
                    <SendForApprovalButton />
                    <MarkAsReviewedButton />
                    <MarkAsEditedButton />
                    <ApprovalControls
                      periodicReview={periodicReview}
                      refetchPeriodicReview={refetchPeriodicReview}
                      versions={versions}
                    />
                    <MoreActionsButton users={users} tags={tags} />
                  </ButtonsContextWrapper>
                  <QOpenPropertiesPanelButton />
                </QButtonGroup>
              </QHeader>
              <QStack direction="row">
                <QBox width={'100%'}>
                  <ResourceRouteTabsSection
                    qualioDocument={qualioDocument}
                    currentUser={currentUser}
                    editorMode={editorMode}
                    updateEditorMode={updateEditorMode}
                    isMultiRootEditorEnabled={isMultiRootEditorEnabled}
                    latestVersion={latestVersion}
                    displayVersion={displayVersion}
                    versions={versions}
                    handleDisplayVersionOptionChange={
                      handleDisplayVersionOptionChange
                    }
                    handleCompareVersionsOptionChange={
                      handleCompareVersionsOptionChange
                    }
                    documentsTaskDrawer={documentsTaskDrawer}
                    location={location}
                  />
                </QBox>
                <DocumentProperties
                  tags={tags}
                  versions={versions}
                  changeControl={{
                    changeId: changeControl?.id,
                    changeControlReference:
                      changeControl?.change_control_reference,
                    templateType: changeControlWithTemplate?.template_type,
                    templateVersion:
                      changeControlWithTemplate?.template_version,
                  }}
                  groups={groups}
                  users={users}
                  periodicReview={periodicReview}
                />
              </QStack>
            </QBodyLayout.Default>
          </ZoomContext.Provider>
        </QMultiRootEditorContextProvider>
      </EditorStatusContext.Provider>
    </DocumentOverviewContext.Provider>
  );
};
